/**
 * ShopScreen.jsx
 * Vollständiger Shop für Mobile App
 * Features: Punkt-Pakete, 10er-Abos, Wiederkehrende Abos, Produkte
 */
import React, { useState, useEffect } from 'react';
import { 
  Wallet, Trophy, Package, Repeat, ShoppingBag, RefreshCw,
  CreditCard, Check, X, AlertTriangle, ChevronRight, Percent,
  Clock, Calendar
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ShopScreen = ({ portalToken, customerId, onNavigate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('points');
  
  // Data
  const [pointPackages, setPointPackages] = useState([]);
  const [creditPackages, setCreditPackages] = useState([]); // 10er-Abos
  const [recurringPackages, setRecurringPackages] = useState([]); // Wiederkehrende
  const [products, setProducts] = useState([]);
  const [recurringDiscount, setRecurringDiscount] = useState(5);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [reEngagementDiscount, setReEngagementDiscount] = useState(null);
  
  // Purchase state
  const [purchasing, setPurchasing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  
  // Discount state
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidating, setDiscountValidating] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  useEffect(() => {
    if (portalToken) loadShop();
  }, [portalToken]);

  const loadShop = async () => {
    setLoading(true);
    try {
      const [shopRes, productsRes, cardsRes] = await Promise.all([
        axios.get(`${API}/portal/shop/packages`, { headers: { Authorization: `Bearer ${portalToken}` } }),
        axios.get(`${API}/portal/shop/products`, { headers: { Authorization: `Bearer ${portalToken}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/payment-methods`, { headers: { Authorization: `Bearer ${portalToken}` } }).catch(() => ({ data: [] }))
      ]);
      
      setPointPackages(shopRes.data.point_packages || []);
      setRecurringDiscount(shopRes.data.recurring_discount || 5);
      
      // Separate templates into 10er-Abos and Wiederkehrende
      const templates = shopRes.data.subscription_templates || [];
      setCreditPackages(templates.filter(t => !t.is_recurring && t.credits > 0));
      setRecurringPackages(templates.filter(t => t.is_recurring));
      
      setProducts(productsRes.data || []);
      setPaymentMethods(cardsRes.data || []);
      
      // Check for re-engagement discount
      const reDiscount = shopRes.data.re_engagement_discount;
      if (reDiscount && reDiscount.is_active) {
        setReEngagementDiscount(reDiscount);
      }
    } catch (error) {
      console.error('Error loading shop:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const openPurchase = (item, type) => {
    setSelectedItem({ ...item, purchaseType: type });
    setDiscountCode('');
    setAppliedDiscount(null);
    setShowPurchaseModal(true);
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountValidating(true);
    try {
      const res = await axios.post(`${API}/discounts/validate`, { code: discountCode.trim() });
      setAppliedDiscount(res.data);
      toast.success(`${res.data.discount_percentage}% Rabatt angewendet!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ungültiger Code');
      setAppliedDiscount(null);
    } finally {
      setDiscountValidating(false);
    }
  };

  const getDiscountedPrice = (price) => {
    if (!appliedDiscount) return price;
    return price * (1 - appliedDiscount.discount_percentage / 100);
  };

  const confirmPurchase = async () => {
    if (!selectedItem) return;
    
    if (paymentMethods.length === 0) {
      toast.error(t('shop.add_payment_first'));
      setShowPurchaseModal(false);
      return;
    }
    
    setPurchasing(true);
    try {
      let endpoint = '';
      let payload = {};
      const discountToApply = appliedDiscount?.code || (reEngagementDiscount?.is_active ? reEngagementDiscount.code : null);
      
      switch (selectedItem.purchaseType) {
        case 'points':
          endpoint = `${API}/portal/shop/purchase-points`;
          payload = { point_package_id: selectedItem.id, discount_code: discountToApply };
          break;
        case 'credits':
          endpoint = `${API}/portal/shop/purchase-credits`;
          payload = { subscription_template_id: selectedItem.id, discount_code: discountToApply };
          break;
        case 'recurring':
          endpoint = `${API}/portal/shop/purchase-subscription`;
          payload = { subscription_template_id: selectedItem.id, discount_code: discountToApply };
          break;
        default:
          throw new Error('Unbekannter Kauftyp');
      }
      
      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      toast.success(t('shop.purchase_success'));
      setShowPurchaseModal(false);
      setSelectedItem(null);
      loadShop();
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || t('shop.purchase_error'));
    } finally {
      setPurchasing(false);
    }
  };

  const tabs = [
    { id: 'points', label: t('shop.tab_points'), icon: Wallet },
    { id: 'credits', label: t('shop.tab_credits'), icon: Package },
    { id: 'recurring', label: t('shop.tab_recurring'), icon: Repeat, badge: `-${recurringDiscount}%` },
    { id: 'products', label: t('shop.tab_products'), icon: ShoppingBag }
  ];

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="shop-screen">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/30 to-orange-500/20 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">{t('shop.title')}</h1>
            <p className="text-zinc-400 text-xs">{t('shop.subtitle')}</p>
          </div>
        </div>
        <button 
          onClick={loadShop}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Re-Engagement Discount Banner */}
      {reEngagementDiscount?.is_active && (
        <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30" data-testid="mobile-reengagement-banner">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-400 font-semibold text-xs">
                {reEngagementDiscount.percent}% Rabatt verfügbar!
              </p>
              <p className="text-green-300/70 text-[10px]">
                Code: <span className="font-mono font-bold">{reEngagementDiscount.code}</span> — einmalig
              </p>
            </div>
            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] rounded-full border border-green-500/30">Aktiv</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-4 px-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap flex items-center gap-1 transition-all ${
              activeTab === tab.id
                ? 'bg-yellow-500 text-black'
                : 'bg-zinc-800 text-zinc-300'
            }`}
          >
            <tab.icon className="w-3 h-3" />
            {tab.label}
            {tab.badge && (
              <span className={`text-[9px] px-1 rounded ${
                activeTab === tab.id ? 'bg-black/20 text-black' : 'bg-green-600 text-white'
              }`}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
          <RefreshCw className="w-6 h-6 text-yellow-400 animate-spin mx-auto mb-2" />
          <p className="text-zinc-400 text-xs">{t('shop.loading_shop')}</p>
        </div>
      ) : (
        <>
          {/* Points Tab */}
          {activeTab === 'points' && (
            <div className="space-y-2">
              {pointPackages.length === 0 ? (
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <Wallet className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-zinc-400 text-xs">{t('shop.no_points')}</p>
                </div>
              ) : (
                pointPackages.map((pkg) => (
                  <div 
                    key={pkg.id}
                    className="bg-zinc-800/50 rounded-lg p-3 flex items-center gap-3 border border-yellow-500/20"
                  >
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center shrink-0">
                      <Wallet className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm">{pkg.points} Punkte</p>
                      {pkg.bonus_points > 0 && (
                        <span className="text-yellow-400 text-[10px] bg-yellow-500/20 px-1.5 py-0.5 rounded-full">
                          +{pkg.bonus_points} {t('shop.bonus')}
                        </span>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-white font-bold text-sm">CHF {pkg.price}</p>
                      <button 
                        onClick={() => openPurchase(pkg, 'points')}
                        className="mt-1 px-3 py-1 bg-yellow-500 text-black text-xs font-medium rounded-lg"
                        data-testid={`buy-points-${pkg.id}`}
                      >
                        {t('shop.buy')}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* 10er-Abo Tab */}
          {activeTab === 'credits' && (
            <div className="space-y-2">
              {creditPackages.length === 0 ? (
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <Package className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-zinc-400 text-xs">{t('shop.no_credits')}</p>
                </div>
              ) : (
                creditPackages.map((pkg) => (
                  <div 
                    key={pkg.id}
                    className="bg-zinc-800/50 rounded-lg p-3 border border-blue-500/20"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                        <Package className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-bold text-sm truncate">{pkg.name}</p>
                        <p className="text-zinc-400 text-[11px]">{pkg.credits}x {pkg.service_name || 'Einheiten'}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-white font-bold text-sm">CHF {pkg.price}</p>
                        <button 
                          onClick={() => openPurchase(pkg, 'credits')}
                          className="mt-1 px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded-lg"
                          data-testid={`buy-credits-${pkg.id}`}
                        >
                          {t('shop.buy')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Recurring Tab */}
          {activeTab === 'recurring' && (
            <div className="space-y-2">
              {/* Discount Banner */}
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/10 rounded-lg p-2.5 border border-green-500/30">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-500/30 rounded-lg flex items-center justify-center">
                    <Percent className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-xs">{t('shop.recurring_discount', { percent: recurringDiscount })}</p>
                    <p className="text-zinc-400 text-[10px]">{t('shop.recurring_hint')}</p>
                  </div>
                </div>
              </div>

              {recurringPackages.length === 0 ? (
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <Repeat className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-zinc-400 text-xs">{t('shop.no_recurring')}</p>
                </div>
              ) : (
                recurringPackages.map((pkg) => {
                  const discountedPrice = pkg.price * (1 - recurringDiscount / 100);
                  return (
                    <div 
                      key={pkg.id}
                      className="bg-zinc-800/50 rounded-lg p-3 border border-green-500/20"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center shrink-0">
                          <Repeat className="w-5 h-5 text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-bold text-sm truncate">{pkg.name}</p>
                          <p className="text-zinc-400 text-[11px]">
                            {pkg.points_per_period || pkg.credits} {pkg.points_per_period ? 'Punkte' : 'Einheiten'} / Monat
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-zinc-500 text-xs line-through">CHF {pkg.price}</p>
                          <p className="text-green-400 font-bold text-sm">CHF {discountedPrice.toFixed(2)}</p>
                          <button 
                            onClick={() => openPurchase(pkg, 'recurring')}
                            className="mt-1 px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-lg"
                            data-testid={`buy-recurring-${pkg.id}`}
                          >
                            {t('shop.tab_recurring')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="space-y-2">
              {products.length === 0 ? (
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <ShoppingBag className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-zinc-400 text-xs">{t('shop.no_products')}</p>
                </div>
              ) : (
                products.map((product) => (
                  <div 
                    key={product.id}
                    className="bg-zinc-800/50 rounded-lg p-3 flex items-center gap-3"
                  >
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center shrink-0">
                        <ShoppingBag className="w-5 h-5 text-purple-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm truncate">{product.name}</p>
                      <p className="text-zinc-400 text-[11px] line-clamp-1">{product.description}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-white font-bold text-sm">CHF {product.price}</p>
                      <button className="mt-1 px-4 py-1.5 bg-purple-500 text-white text-sm font-medium rounded-lg">
                        {t('shop.buy')}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Purchase Confirmation Modal */}
      {showPurchaseModal && selectedItem && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-end justify-center">
          <div className="bg-zinc-900 rounded-t-3xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">{t('shop.confirm_purchase')}</h2>
              <button onClick={() => setShowPurchaseModal(false)} className="p-2">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="bg-zinc-800/50 rounded-xl p-4 mb-4">
              <p className="text-white font-bold text-lg">{selectedItem.name || `${selectedItem.points} Punkte`}</p>
              {selectedItem.purchaseType === 'recurring' && (
                <div className="flex items-center gap-2 mt-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  <p className="text-orange-300 text-sm">{t('account.min_duration')}</p>
                </div>
              )}
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-zinc-700">
                <span className="text-zinc-400">{t('shop.price')}</span>
                <div className="text-right">
                  {appliedDiscount ? (
                    <>
                      <span className="text-zinc-500 line-through text-sm mr-2">
                        CHF {selectedItem.purchaseType === 'recurring'
                          ? (selectedItem.price * (1 - recurringDiscount / 100)).toFixed(2)
                          : selectedItem.price}
                      </span>
                      <span className="text-green-400 font-bold text-xl">
                        CHF {getDiscountedPrice(
                          selectedItem.purchaseType === 'recurring'
                            ? selectedItem.price * (1 - recurringDiscount / 100)
                            : selectedItem.price
                        ).toFixed(2)}
                      </span>
                    </>
                  ) : (
                    <span className="text-white font-bold text-xl">
                      CHF {selectedItem.purchaseType === 'recurring' 
                        ? (selectedItem.price * (1 - recurringDiscount / 100)).toFixed(2)
                        : selectedItem.price}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Discount Code */}
            <div className="bg-zinc-800/50 rounded-xl p-3 mb-4" data-testid="discount-code-section">
              <p className="text-zinc-400 text-xs mb-2 flex items-center gap-1">
                <Percent className="w-3 h-3" /> {t('shop.discount_code')}
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder={t('shop.enter_code')}
                  className="flex-1 bg-zinc-700 text-white text-sm rounded-lg px-3 py-2 placeholder:text-zinc-500 border-none outline-none"
                  data-testid="discount-code-input"
                />
                <button
                  onClick={handleApplyDiscount}
                  disabled={discountValidating || !discountCode.trim()}
                  className="px-4 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                  data-testid="discount-apply-btn"
                >
                  {discountValidating ? '...' : t('shop.redeem')}
                </button>
              </div>
              {appliedDiscount && (
                <p className="text-green-400 text-xs mt-2" data-testid="discount-applied-msg">
                  {t('shop.discount_active', { percent: appliedDiscount.discount_percentage, code: appliedDiscount.code })}
                </p>
              )}
            </div>

            {paymentMethods.length === 0 ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
                <p className="text-red-300 text-sm">
                  {t('shop.add_payment')}
                </p>
              </div>
            ) : (
              <div className="bg-zinc-800/50 rounded-xl p-3 mb-4 flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-white text-sm">{t('shop.payment_with')}</p>
                  <p className="text-zinc-400 text-xs">
                    {paymentMethods[0]?.brand || 'Karte'} •••• {paymentMethods[0]?.last4 || '****'}
                  </p>
                </div>
              </div>
            )}
            
            <button
              onClick={confirmPurchase}
              disabled={purchasing || paymentMethods.length === 0}
              className="w-full py-4 bg-yellow-500 text-black rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="confirm-purchase-btn"
            >
              {purchasing ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  {t('shop.buy_now')}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopScreen;
